import { Badge } from "@/common/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/common/ui/table";
import { formatTimestamp } from "@/lib/utils";

import type { ConversationLogEntry } from "./conversation-logs-types";

interface LogsTableProps {
  entries: ConversationLogEntry[];
  selectedEntry: ConversationLogEntry | null;
  onSelect: (entry: ConversationLogEntry) => void;
}

function actionBadgeVariant(action: string): "default" | "secondary" | "outline" {
  switch (action) {
    case "query":
      return "default";
    case "tool_call":
      return "secondary";
    default:
      return "outline";
  }
}

function actionLabel(action: string): string {
  switch (action) {
    case "query":
      return "Query";
    case "tool_call":
      return "Tool Call";
    case "prefill_generate":
      return "Prefill";
    default:
      return action;
  }
}

export function LogsTable({
  entries,
  selectedEntry,
  onSelect,
}: LogsTableProps): React.ReactElement {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Time</TableHead>
          <TableHead>User</TableHead>
          <TableHead>Session</TableHead>
          <TableHead>Action</TableHead>
          <TableHead>Model</TableHead>
          <TableHead>Guardrail</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {entries.map((entry, index) => {
          const isSelected =
            selectedEntry?.session_id === entry.session_id &&
            selectedEntry?.timestamp === entry.timestamp;

          return (
            <TableRow
              key={`${entry.session_id}-${entry.timestamp}`}
              data-testid={`log-row-${index}`}
              className={`cursor-pointer ${isSelected ? "bg-muted" : ""}`}
              onClick={() => onSelect(entry)}
            >
              <TableCell className="whitespace-nowrap text-xs">
                {formatTimestamp(entry.timestamp)}
              </TableCell>
              <TableCell className="max-w-[150px] truncate font-mono text-xs">
                {entry.user_id}
              </TableCell>
              <TableCell className="max-w-[120px] truncate font-mono text-xs">
                {entry.session_id.slice(0, 8)}
              </TableCell>
              <TableCell>
                <Badge variant={actionBadgeVariant(entry.action)}>
                  {actionLabel(entry.action)}
                </Badge>
              </TableCell>
              <TableCell className="text-xs">{entry.model_id}</TableCell>
              <TableCell>
                {entry.guardrail_intervention ? (
                  <Badge variant="destructive" data-testid={`guardrail-badge-${index}`}>
                    {entry.guardrail_intervention.action}
                  </Badge>
                ) : (
                  <span className="text-xs text-muted-foreground">-</span>
                )}
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}
